import React, { useState, useContext, useEffect, useRef, Suspense, lazy } from 'react';
import { Skeleton, Box, Button, Typography, Modal, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { useQuery, useQueryClient } from '@tanstack/react-query'; 
import AuthContext from "./auth/Context";
import fetchXacThuc from './auth/fetchXacThuc';

// ImageViewer import ngược lại SecureImage => nạp động để tránh vòng lặp phụ thuộc,
// đồng thời chỉ tải mã trình xem ảnh khi người dùng thực sự mở xem full.
const ImageViewer = lazy(() => import('./ImageViewer'));

export default function SecureImage({ 
  src, 
  alt = "Secure Image", 
  style,
  sx = {},
  size = "S",
  className, 
  onLoad, 
  onError,
  canFullView=false
}) {
  const queryClient = useQueryClient();
  const context = useContext(AuthContext);
  
  // Trạng thái kiểm soát khi thẻ <img> bị lỗi decode
  const [hasRenderError, setHasRenderError] = useState(false);

  // Kiểm tra cache từ React Query
  const cachedBlob = queryClient.getQueryData(['secure-image', src, size]);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isVisible, setIsVisible] = useState(!!cachedBlob); 
  const imgRef = useRef(null);

  // Trạng thái cho Full View Modal (nội dung do ImageViewer đảm nhiệm)
  const [openFullView, setOpenFullView] = useState(false);

  // 1. LAZY LOAD OBSERVER
  useEffect(() => {
    if (isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); 
        }
      },
      { rootMargin: '100px 0px' } 
    );

    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  // Hàm Helper tạo đường dẫn full URL
  const getFullUrl = (actionType = 'view', _size) => {
    let finalPath = src;
    if (finalPath.indexOf("http") < 0) {
      const token = context.userInfo?.token || context.config?.public_token || '';
      const serverUrl = context.config?.server_url || '';
      finalPath = `${serverUrl}${src}?access_token=${token}&size=${_size || size}`;
    }
    if(_size) finalPath = finalPath.replace(`size=${size}`,`size=${_size}`);
    if (actionType === 'download') {
      finalPath += `&action=download`;
    }
    return finalPath;
  };

  // 2. REACT QUERY: FETCH & CACHE BLOB THUMBNAIL
  const { data: imageBlob, isLoading: isFetching, isError } = useQuery({
    queryKey: ['secure-image', src, size],
    queryFn: async () => {
      const finalPath = getFullUrl('view');
      // Token hết hạn: fetchXacThuc tự làm mới + chạy lại; không cứu được thì mở form đăng nhập (auth/fetchXacThuc.js).
      const response = await fetchXacThuc(context?.apis, finalPath);
      
      if (!response.ok) {
        console.error("[SecureImage] Lỗi tải image", finalPath, response);
        throw new Error(`Lỗi HTTP: ${response.status}`);
      } 
      
      const blob = await response.blob();
      
      if (!blob.type.startsWith('image/')) {
        console.error("[SecureImage] Định dạng không hỗ trợ render trực tiếp:", blob.type, finalPath);
        throw new Error(`Định dạng không hỗ trợ hiển thị: ${blob.type}`);
      }

      return blob;
    },
    enabled: isVisible && !!src, 
    staleTime: 24 * 60 * 60 * 1000, 
    gcTime: 24 * 60 * 60 * 1000, 
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // 3. QUẢN LÝ BỘ NHỚ URL (Chống Memory Leak cho ảnh thumbnail)
  useEffect(() => {
    let objectUrl = null;
    if (imageBlob) {
      objectUrl = URL.createObjectURL(imageBlob);
      setBlobUrl(objectUrl);
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageBlob]);

  // Xử lý khi bấm nút Tải xuống
  const handleDownload = (e) => {
    if (e) e.stopPropagation();
    const downloadUrl = getFullUrl('download', "FULL");
    context.apis?.downloadFile ? context.apis.downloadFile(downloadUrl) : window.open(downloadUrl, '_blank');
  };

  // 4. XEM ẢNH FULL: mở modal chứa ImageViewer.
  // ImageViewer render một SecureImage với size="FULL" nên phần tải & cache blob
  // vẫn đi qua React Query như cũ (cùng queryKey ['secure-image', src, 'FULL']).
  const handleFullView = (e) => {
    if (e) e.stopPropagation();
    setOpenFullView(true);
  };

  const handleCloseFullView = (e) => {
    if (e) e.stopPropagation();
    setOpenFullView(false);
  };

  // 5. RENDER GIAO DIỆN

  // 5.1 Loading Skeleton
  if (isFetching || !isVisible || (!blobUrl && !isError && !hasRenderError)) {
    return (
      <Box ref={imgRef} sx={sx} style={{ ...style, display: 'inline-block' }} className={className}>
        <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: '100%', minHeight: 120, borderRadius: style?.borderRadius || 1 }} />
      </Box>
    );
  }

  // 5.2 Giao diện KHI GẶP LỖI
  if (isError || hasRenderError) {
    if (onError) setTimeout(() => onError(new Error("Lỗi render hình ảnh")), 0);

    return (
      <Box 
        ref={imgRef} 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          border: '1px dashed #d0d7de',
          borderRadius: style?.borderRadius || 1.5,
          p: 2,
          minHeight: 140,
          textAlign: 'center',
          gap: 1
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 36, color: '#8c959f' }} />
        <Typography variant="caption" sx={{ color: '#57606a', fontWeight: 500 }}>
          Không thể hiển thị ảnh
        </Typography>
        
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{
            fontSize: '0.75rem',
            textTransform: 'none',
            borderRadius: 1.5,
            color: '#0969da',
            borderColor: '#d0d7de',
            '&:hover': {
              borderColor: '#0969da',
              backgroundColor: 'rgba(9, 105, 218, 0.04)',
            }
          }}
        >
          Tải file gốc
        </Button>
      </Box>
    );
  }

  // 5.3 Giao diện Hiển thị Ảnh bình thường & Lightbox Modal
  return (
    <>
      <Box 
        ref={imgRef}
        component="img"
        src={blobUrl} 
        alt={alt} 
        style={style}
        className={className}
        sx={{
          cursor: 'pointer',
          transition: 'transform 0.2s ease, filter 0.2s ease',
          '&:hover': {
            filter: 'brightness(0.95)',
          },
          animation: cachedBlob ? 'none' : 'secureFadeIn 0.8s ease-out forwards',
          '@keyframes secureFadeIn': {
            '0%': { opacity: 0, filter: 'blur(10px)' },
            '100%': { opacity: 1, filter: 'blur(0)' }
          },
          ...sx
        }}
        onLoad={(e) => { 
          if (onLoad) onLoad(e); 
        }}
        onError={() => {
          setHasRenderError(true);
          if (onError) onError(new Error("Lỗi render hình ảnh"));
        }}
        onClick={canFullView?handleFullView:undefined}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />

      {/* MODAL LIGHTBOX: dùng ImageViewer (zoom / xoay / kéo thả / pinch / chia sẻ) */}
      {canFullView && (
        <Modal
          open={openFullView}
          onClose={handleCloseFullView}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(4px)',
            zIndex: 19000
          }}
        >
          <Box sx={{ position: 'relative', width: '100vw', height: '100vh', outline: 'none' }}>
            <Suspense
              fallback={
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, color: '#fff' }}>
                  <CircularProgress color="inherit" size={48} />
                  <Typography variant="body2">Đang tải ảnh chất lượng cao...</Typography>
                </Box>
              }
            >
              <ImageViewer
                src={src}
                alt={alt}
                size="FULL"
                onClose={handleCloseFullView}
                onDownload={handleDownload}
              />
            </Suspense>
          </Box>
        </Modal>
      )}
    </>
  );
}